import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Add as AddIcon,
  AccountTree as TreeIcon,
  People as RelationshipsIcon,
  Event as EventIcon,
  PhotoCamera as PhotoIcon,
  Description as DocumentIcon,
  AutoStories as MemoryIcon,
  Note as NoteIcon,
  Comment as CommentIcon,
  Science as ResearchIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import type { PersonPageState, PermissionFlags } from '../PersonPage.types';
import type { ModalHandlers } from './Sidebar';

interface QuickLinksCardProps {
  personId: string;
  counts: PersonPageState['counts'];
  permissions: PermissionFlags;
  modalHandlers: ModalHandlers;
}

interface QuickLinkItem {
  icon: React.ReactElement;
  label: string;
  viewRoute: string;
  addHandler?: () => void;
  canAdd?: boolean;
  count?: number;
}

/**
 * QuickLinksCard - Combined Quick Add and Shortcuts panel
 *
 * Each entity type shows:
 * - View All: Navigate to full tab page
 * - Add: Open modal for creating new content
 *
 * Replaces the separate QuickAddCard and ShortcutsCard components
 */
export const QuickLinksCard: React.FC<QuickLinksCardProps> = ({
  personId,
  counts,
  permissions,
  modalHandlers
}) => {
  const navigate = useNavigate();

  const links: QuickLinkItem[] = [
    {
      icon: <RelationshipsIcon fontSize="small" />,
      label: 'Relationships',
      viewRoute: `/people/${personId}/relationships`,
      addHandler: modalHandlers.openRelationshipBuilder,
      canAdd: permissions.canEdit
    },
    {
      icon: <EventIcon fontSize="small" />,
      label: 'Events',
      viewRoute: `/people/${personId}/timeline`,
      addHandler: modalHandlers.openEventForm,
      canAdd: permissions.canEdit,
      count: counts.timeline
    },
    {
      icon: <PhotoIcon fontSize="small" />,
      label: 'Photos',
      viewRoute: `/people/${personId}/photos`,
      addHandler: modalHandlers.openPhotoUpload,
      canAdd: permissions.canUpload,
      count: counts.photos
    },
    {
      icon: <DocumentIcon fontSize="small" />,
      label: 'Documents',
      viewRoute: `/people/${personId}/documents`,
      addHandler: modalHandlers.openDocumentUpload,
      canAdd: permissions.canUpload,
      count: counts.documents
    },
    {
      icon: <MemoryIcon fontSize="small" />,
      label: 'Memories',
      viewRoute: `/people/${personId}/memories`,
      addHandler: modalHandlers.openMemoryEditor,
      canAdd: permissions.canEdit,
      count: counts.memories
    },
    {
      icon: <NoteIcon fontSize="small" />,
      label: 'Notes',
      viewRoute: `/people/${personId}/notes`,
      addHandler: modalHandlers.openNoteEditor,
      canAdd: permissions.canEdit,
      count: counts.notes
    },
    {
      icon: <CommentIcon fontSize="small" />,
      label: 'Comments',
      viewRoute: `/people/${personId}/comments`,
      addHandler: modalHandlers.openCommentForm,
      canAdd: permissions.canEdit,
      count: counts.comments
    },
    {
      icon: <ResearchIcon fontSize="small" />,
      label: 'Research',
      viewRoute: `/people/${personId}/research`,
      addHandler: modalHandlers.openResearchEditor,
      canAdd: permissions.canEdit,
      count: counts.research
    },
    {
      icon: <HistoryIcon fontSize="small" />,
      label: 'History',
      viewRoute: `/people/${personId}/history`,
      // History is view-only
      canAdd: false
    }
  ];

  // Special "View in Tree" link
  const handleViewInTree = () => {
    navigate(`/tree/${personId}`);
  };

  return (
    <Card>
      <CardHeader
        title="Quick Links"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
      />
      <CardContent sx={{ pt: 0, pb: '8px !important' }}>
        <List dense sx={{ py: 0 }}>
          {/* View in Tree - Special Item */}
          <ListItem
            sx={{
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { bgcolor: 'action.hover' },
              borderRadius: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <TreeIcon fontSize="small" sx={{ color: 'action.active' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                View in Tree
              </Typography>
            </Box>
            <Tooltip title="View in Family Tree">
              <IconButton
                size="small"
                onClick={handleViewInTree}
                sx={{ ml: 1 }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListItem>

          {/* All Other Links */}
          {links.map((link) => (
            <ListItem
              key={link.label}
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                {React.cloneElement(link.icon, { sx: { color: 'action.active' } })}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {link.label}
                  {link.count !== undefined && link.count > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5, color: 'text.secondary' }}
                    >
                      ({link.count})
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {/* View All Button */}
                <Tooltip title={`View All ${link.label}`}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(link.viewRoute)}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Add Button (if applicable) */}
                {link.addHandler && link.canAdd && (
                  <Tooltip title={`Add ${link.label.endsWith('s') ? link.label.slice(0, -1) : link.label}`}>
                    <IconButton
                      size="small"
                      onClick={link.addHandler}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
